import { Injectable, isDevMode } from '@angular/core';
import { CentersCentersDaoService } from 'src/app/dao/centers-dao-services/centers-centers-dao-service/centers-centers-dao.service';
import { EMPTY, Subject } from 'rxjs';
import { UserService } from 'src/app/services/user-service/user.service';
import { Center } from 'src/app/models/center-model/center.model';
import { Schedule } from 'src/app/models/schedule-models/schedule.model';
import { CenterLanguage } from 'src/app/models/center-model/center-language.model';
import { CenterType } from 'src/app/models/center-model/center-type';
import { UserStorageService } from '../../user-storage-service/user-storage.service';
import { map } from 'rxjs/internal/operators/map';
import { tap } from 'rxjs/internal/operators/tap';
import { CenterCurrency } from 'src/app/models/center-model/center-currency.model';
import { CenterAlertIotDevice } from 'src/app/models/center-model/center-alert-iot-device.model';
import { CenterDeleteImpact } from 'src/app/models/center-model/center-delete-impact.model';

@Injectable({
  providedIn: 'root'
})
export class CentersCentersService {

  accessToken = 'CEN';
  refreshCenterListener = new Subject<any>();
  filterCentersListener = new Subject<any>();
  searchCentersListener = new Subject<any>();
  toggleCentersDispositionListener = new Subject<any>();

  constructor(private centersCentersDao: CentersCentersDaoService,
    private userStorageService: UserStorageService,
    private userService: UserService) { }

  public getCenters() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getCenters(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const centers = new Array<Center>();
          o['data']['centerList'].forEach(
            (c) => {
              centers.push(Object.assign(new Center(), c));
            }
          );
          return centers;
        })
      );
    }
    return EMPTY;
  }

  public getCenterInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getCenterInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const centerLang = new CenterLanguage();
          Object.assign(centerLang, o['data']['centerDetails']['defaultLanguage']);
          const centerCurrency = new CenterCurrency();
          Object.assign(centerCurrency, o['data']['centerDetails']['currency']);
          const center = new Center();
          Object.assign(center, o['data']['centerDetails']);
          const centerLanguages = new Array<CenterLanguage>();
          o['data']['centerDetails']['centerLanguageList'].forEach(
            (l) => {
              centerLanguages.push(Object.assign(new CenterLanguage(), l));
            }
          );
          center.CenterLanguageList = centerLanguages;
          center.DefaultLanguage = centerLang;
          center.Currency = centerCurrency;
          return center;
        })
      );
    }
    return EMPTY;
  }

  public getCenterDeleteImpact(internalId: string) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getCenterDeleteImpact(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        map(response => {
          if (!response || response['status'] !== 200 || response['error'] !== 'OK') {
            throw new Error(response && response['message'] || 'stores-page.delete center impact failed');
          }
          const impact = response['data'] && response['data']['centerDeleteImpact'];
          if (!impact || impact.internalId !== internalId || !Array.isArray(impact.impactList)
              || !impact.impactList.every(item => item && typeof item.resourceType === 'string'
                && typeof item.impactType === 'string' && Number.isInteger(item.affectedCenters)
                && item.affectedCenters >= 0 && Number.isInteger(item.affectedDesks)
                && item.affectedDesks >= 0)) {
            throw new Error('stores-page.delete center impact failed');
          }
          return impact as CenterDeleteImpact;
        })
      );
    }
    return EMPTY;
  }

  public deleteCenter(internalId: string) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.deleteCenter(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        map(response => {
          if (!response || response['status'] !== 200 || response['error'] !== 'OK') {
            throw new Error(response && response['message'] || 'stores-page.delete center failed');
          }
          return response;
        }),
        tap(() => {
          const centers = this.userStorageService.getCenterList();
          if (centers) {
            this.userStorageService.setCenterList(centers.filter(center => center.InternalId !== internalId));
          }
          this.refreshCenter();
        })
      );
    }
    return EMPTY;
  }

  public addNewCenter(visible, imageSource, centerName, cif, fiscalName, fiscalAdress, street, zipCode, city, province, region, country, gpsLatitude, gpsLongitude, faxNumber, phoneNumber, centerMail, centerContactName, centerTypeInternalId, code, timeZone, currency) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.addNewCenter(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), visible, imageSource, centerName, cif, fiscalName, fiscalAdress, street, zipCode, city, province, region, country, gpsLatitude, gpsLongitude, faxNumber, phoneNumber, centerMail, centerContactName, centerTypeInternalId, code, timeZone, currency).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateCenterInfo(internalId, visible, centerName, cif, fiscalName, fiscalAdress, street, zipCode, city, province, region, country, gpsLatitude, gpsLongitude, faxNumber, phoneNumber, centerMail, centerContactName, centerType, timeZone, currency) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.updateCenterInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, visible, centerName, cif, fiscalName, fiscalAdress, street, zipCode, city, province, region, country, gpsLatitude, gpsLongitude, faxNumber, phoneNumber, centerMail, centerContactName, centerType, timeZone, currency).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateCenterRadius(internalId, maxRadius) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.updateCenterRadius(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, maxRadius).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getCenterScheduleList(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getCenterScheduleList(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const schedules = new Array<Schedule>();
          o['data']['calendarList'].forEach(
            (s) => {
              schedules.push(Object.assign(new Schedule(), s));
            }
          );
          return schedules;
        })
      );
    }
    return EMPTY;
  }

  public getCenterScheduleInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getCenterScheduleInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const schedule = new Schedule();
          Object.assign(schedule, o['data']['centerCalendar']);
          return schedule;
        })
      );
    }
    return EMPTY;
  }

  public addNewCenterSchedule(relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.addNewCenterSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateCenterSchedule(internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.updateCenterSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateCenterDefaultLanguage(centerInternalId, defaultLanguageInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.updateCenterDefaultLanguage(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalId, defaultLanguageInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public linkExistingCenterSchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.linkExistingCenterSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteCenterSchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.deleteCenterSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getCenterConfigurationInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getCenterConfigurationInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const languages = new Array<CenterLanguage>();
          o['data']['languageList'].forEach(
            (l) => {
              const language = new CenterLanguage();
              Object.assign(language, l);
              languages.push(language);
            }
          );
          const currencies = new Array<CenterCurrency>();
          o['data']['currencyList'].forEach(
            (c) => {
              const currency = new CenterCurrency();
              Object.assign(currency, c);
              currencies.push(currency);
            }
          );
          return { languages, currencies };
        })
      );
    }
    return EMPTY;
  }

  public getCenterTypes() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getCenterTypes(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const centerTypes = new Array<CenterType>();
          o['data']['centerTypeList'].forEach(
            (ct) => {
              const centerType = new CenterType();
              Object.assign(centerType, ct);
              centerTypes.push(centerType);
            }
          );
          return centerTypes;
        })
      );
    }
    return EMPTY;
  }

  public getCenterCurrencies() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getCenterCurrencies(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const centerCurrencies = new Array<CenterCurrency>();
          o['data']['currencyList'].forEach(
            (cc) => {
              const centerCurrency = new CenterCurrency();
              Object.assign(centerCurrency, cc);
              centerCurrencies.push(centerCurrency);
            }
          );
          return centerCurrencies;
        })
      );
    }
    return EMPTY;
  }

  public getCenterExistingSchedules(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getCenterExistingSchedules(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const schedules = new Array<Schedule>();
          o['data']['calendarList'].forEach(
            (ct) => {
              const schedule = new Schedule();
              Object.assign(schedule, ct);
              schedules.push(schedule);
            }
          );
          return schedules;
        })
      );
    }
    return EMPTY;
  }

  public saveCenterLanguages(centerInternalId, languageInternalIdList) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.saveCenterLanguages(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalId, languageInternalIdList).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateInfoAlert(centerInternalId, infoAlert) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.updateInfoAlert(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalId, infoAlert).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateShowOnline(centerInternalId, online) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.updateShowOnline(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), centerInternalId, online).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateImageCenter(context, lang, udid, internalId, imageRaw, size) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.updateImageCenter(context, lang, udid, internalId, imageRaw, size).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getIotDevices(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.centersCentersDao.getIotDevices(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const iotDevices = new Array<CenterAlertIotDevice>();
          o['data']['iotDevices'].forEach(
            (i) => {
              const iotDevice = new CenterAlertIotDevice();
              Object.assign(iotDevice, i);
              iotDevices.push(iotDevice);
            }
          );
          return iotDevices;
        })
      );
    }
    return EMPTY;
  }

  public refreshCenter() {
    this.refreshCenterListener.next();
  }

  public refreshCenterListen() {
    return this.refreshCenterListener.asObservable();
  }

  public filterCenters(filterKey) {
    this.filterCentersListener.next(filterKey);
  }

  public filterCentersListen() {
    return this.filterCentersListener.asObservable();
  }

  public searchCenters(filterKey) {
    this.searchCentersListener.next(filterKey);
  }

  public searchCentersListen() {
    return this.searchCentersListener.asObservable();
  }
  public toggleCentersDisposition(viewDis) {
    this.toggleCentersDispositionListener.next(viewDis);
  }
  public toggleCentersDispositionListen() {
    return this.toggleCentersDispositionListener.asObservable();
  }
}
